import { useCallback, useMemo, useState } from 'react';

import { useCrossFlow } from '@atlassiansox/cross-flow-support';
import type { TouchpointSourceType } from '@trello/ad-sequencer';
import type { ExperimentVariations } from '@trello/feature-gate-client';
import { useGetExperimentValue } from '@trello/feature-gate-client';
import { useHasValidAaSession } from '@trello/heartbeat/session';

import {
  type TrelloCrossFlowExperiment,
  trelloCrossFlowOpen,
} from '../TrelloCrossFlow';
import { isSourceTargetedByFeatureKey } from './isSourceTargetedByFeatureFlag';

const experimentFeatureKey = 'trello_xf_1p_ads_in_madshot_v2';

export type WelcomeScreenTaskSelectionExperimentVariations =
  ExperimentVariations<typeof experimentFeatureKey, 'cohort'>;

export interface CrossFlowForWelcomeScreenTaskSelectionParams {
  source: TouchpointSourceType;
}

export const useCrossFlowForWelcomeScreenTaskSelection = ({
  source,
}: CrossFlowForWelcomeScreenTaskSelectionParams): TrelloCrossFlowExperiment<WelcomeScreenTaskSelectionExperimentVariations> => {
  const crossFlow = useCrossFlow();

  const [fireExposureEvent, setFireExposureEvent] = useState(false);
  const [hasValidAaSession] = useHasValidAaSession();

  const { cohort } = useGetExperimentValue({
    experimentName: experimentFeatureKey,
    parameter: 'cohort',
    fireExposureEvent,
  });

  const isRuntimeEligibilityChecksPassed = useMemo(
    () =>
      crossFlow.isEnabled &&
      cohort !== 'not-enrolled' &&
      isSourceTargetedByFeatureKey({
        featureKey: experimentFeatureKey,
        source,
      }),
    [cohort, crossFlow.isEnabled, source],
  );

  const getIsTargeted = useCallback(() => {
    return cohort !== 'not-enrolled';
  }, [cohort]);

  const getExperimentCohort = useCallback(() => {
    if (isRuntimeEligibilityChecksPassed) {
      setFireExposureEvent(true);
      return cohort;
    }

    return 'not-enrolled';
  }, [cohort, isRuntimeEligibilityChecksPassed]);

  return useMemo(
    () => ({
      isEnrolled: isRuntimeEligibilityChecksPassed,
      experimentName: experimentFeatureKey,
      getIsTargeted,
      getExperimentCohort,
      open: trelloCrossFlowOpen(crossFlow, source, [], hasValidAaSession, {
        isNewTrelloUser: true,
      }),
    }),
    [
      crossFlow,
      getExperimentCohort,
      getIsTargeted,
      isRuntimeEligibilityChecksPassed,
      source,
      hasValidAaSession,
    ],
  );
};
